/**
 * AdminUpcomingList — compact “próximos” list for admin (bills, renewals, tasks).
 * BudgetForge-inspired; uses black-glass rows.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface AdminUpcomingItem {
  id: string | number;
  title: string;
  /** ISO date or short label (e.g. "12 jul"). */
  when: string;
  /** Right-side amount or meta. */
  meta?: ReactNode;
  /** Optional tone chip. */
  status?: string;
  statusTone?: "default" | "good" | "warn" | "bad";
  icon?: ReactNode;
}

const STATUS_CLS: Record<NonNullable<AdminUpcomingItem["statusTone"]>, string> = {
  default: "bg-white/10 text-white/50",
  good: "bg-emerald-500/15 text-emerald-300",
  warn: "bg-amber-500/15 text-amber-300",
  bad: "bg-red-500/15 text-red-300",
};

export function AdminUpcomingList({
  items,
  emptyLabel = "Nada próximo 🎉",
  onItemClick,
  className,
  max = 8,
}: {
  items: AdminUpcomingItem[];
  emptyLabel?: string;
  onItemClick?: (item: AdminUpcomingItem) => void;
  className?: string;
  max?: number;
}) {
  const rows = items.slice(0, max);

  if (rows.length === 0) {
    return (
      <div
        className={cn(
          "rounded-xl border border-dashed border-white/10 px-4 py-8 text-center text-[13px] text-white/35",
          className,
        )}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <ul className={cn("admin-upcoming divide-y divide-white/[0.06] rounded-xl border border-white/[0.07] bg-white/[0.02]", className)}>
      {rows.map((item) => {
        const body = (
          <>
            {item.icon != null && (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-base">
                {item.icon}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold text-white/90">{item.title}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-white/40">
                <span className="tabular-nums">{item.when}</span>
                {item.status && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase",
                      STATUS_CLS[item.statusTone ?? "default"],
                    )}
                  >
                    {item.status}
                  </span>
                )}
              </div>
            </div>
            {item.meta != null && (
              <div className="shrink-0 tabular-nums text-[13px] font-semibold text-white/80">{item.meta}</div>
            )}
          </>
        );
        const rowCls = "flex w-full items-center gap-3 px-3 py-2.5 text-left transition";
        return (
          <li key={item.id}>
            {onItemClick ? (
              <button
                type="button"
                onClick={() => onItemClick(item)}
                className={cn(rowCls, "hover:bg-white/[0.04] focus-visible:bg-white/[0.04] focus-visible:outline-none")}
              >
                {body}
              </button>
            ) : (
              <div className={rowCls}>{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
